import React, { useEffect, useState } from "react";

import { doc, getDoc, updateDoc, serverTimestamp } from "firebase/firestore";

import { useNavigate, useParams } from "react-router-dom";

import { FaArrowLeft, FaSave, FaImage } from "react-icons/fa";

import { db } from "../../../firebase/firebase";

import { uploadToCloudinary } from "../../../utils/uploadToCloudinary";

import { generateBlogSlug, tagsToString } from "../../../data/blogs";

import "./Blogs.css";

const EditBlog = () => {
  const navigate = useNavigate();

  const { id } = useParams();

  const [formData, setFormData] = useState({
    title: "",

    slug: "",

    excerpt: "",

    content: "",

    category: "",

    author: "Quality Sports Surface",

    tags: "",

    seoTitle: "",

    seoDescription: "",

    image: "",

    status: "draft",

    published: false,

    featured: false,

    date: "",
  });

  const [imageFile, setImageFile] = useState(null);

  const [imagePreview, setImagePreview] = useState("");

  const [loading, setLoading] = useState(true);

  const [saving, setSaving] = useState(false);

  const [error, setError] = useState("");

  // ============================================================
  // LOAD BLOG
  // ============================================================

  useEffect(() => {
    const loadBlog = async () => {
      try {
        setLoading(true);

        const blogRef = doc(db, "blogs", id);

        const snapshot = await getDoc(blogRef);

        if (!snapshot.exists()) {
          setError("Blog not found.");

          return;
        }

        const data = snapshot.data();

        const published =
          data.published === true || data.status === "published";

        setFormData({
          title: data.title || "",

          slug: data.slug || "",

          excerpt: data.excerpt || "",

          content: data.content || "",

          category: data.category || "",

          author: data.author || "Quality Sports Surface",

          tags: tagsToString(data.tags),

          seoTitle: data.seoTitle || "",

          seoDescription: data.seoDescription || "",

          image: data.image || data.featuredImage || "",

          status: data.status || (published ? "published" : "draft"),

          published,

          featured: data.featured === true,

          date: data.date || "",
        });

        setImagePreview(data.image || data.featuredImage || "");
      } catch (err) {
        console.error("Error loading blog:", err);

        setError("Unable to load blog.");
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      loadBlog();
    }
  }, [id]);

  // ============================================================
  // HANDLE INPUT
  // ============================================================

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    setFormData((previous) => ({
      ...previous,

      [name]: type === "checkbox" ? checked : value,
    }));
  };

  // ============================================================
  // IMAGE
  // ============================================================

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];

    if (!file) {
      return;
    }

    if (!file.type.startsWith("image/")) {
      setError("Please select a valid image.");

      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      setError("Image size must be less than 10 MB.");

      return;
    }

    setError("");

    setImageFile(file);

    setImagePreview(URL.createObjectURL(file));
  };

  // ============================================================
  // SAVE CHANGES
  // ============================================================

  const handleSubmit = async (e) => {
    e.preventDefault();

    setError("");

    if (!formData.title.trim()) {
      setError("Please enter a blog title.");

      return;
    }

    if (!formData.excerpt.trim()) {
      setError("Please enter a short description.");

      return;
    }

    if (!formData.content.trim()) {
      setError("Please enter blog content.");

      return;
    }

    if (!formData.category.trim()) {
      setError("Please enter a category.");

      return;
    }

    try {
      setSaving(true);

      // ======================================================
      // IMAGE
      // ======================================================

      let imageUrl = formData.image.trim();

      if (imageFile) {
        imageUrl = await uploadToCloudinary(imageFile, "image");
      }

      // ======================================================
      // TAGS
      // ======================================================

      const tagsArray = formData.tags
        .split(",")
        .map((tag) => tag.trim())
        .filter(Boolean);

      // ======================================================
      // PUBLISHED
      // ======================================================

      const isPublished = formData.status === "published";

      // ======================================================
      // UPDATE FIREBASE
      // ======================================================

      const blogRef = doc(db, "blogs", id);

      await updateDoc(blogRef, {
        title: formData.title.trim(),

        slug: formData.slug.trim() || generateBlogSlug(formData.title),

        excerpt: formData.excerpt.trim(),

        content: formData.content.trim(),

        category: formData.category.trim(),

        author: formData.author.trim() || "Quality Sports Surface",

        tags: tagsArray,

        seoTitle: formData.seoTitle.trim() || formData.title.trim(),

        seoDescription:
          formData.seoDescription.trim() || formData.excerpt.trim(),

        image: imageUrl,

        status: formData.status,

        published: isPublished,

        featured: formData.featured,

        date: formData.date || "",

        updatedAt: serverTimestamp(),

        publishedAt: isPublished ? serverTimestamp() : null,
      });

      navigate("/admin/blogs");
    } catch (err) {
      console.error("Update blog error:", err);

      setError(err.message || "Unable to update blog.");
    } finally {
      setSaving(false);
    }
  };

  // ============================================================
  // LOADING
  // ============================================================

  if (loading) {
    return (
      <div className="blogs-loading">
        <div className="blogs-spinner"></div>

        <p>Loading blog...</p>
      </div>
    );
  }

  // ============================================================
  // ERROR
  // ============================================================

  if (error && !formData.title) {
    return (
      <div className="blog-form-page">
        <div className="blog-form-error">{error}</div>

        <button
          type="button"
          className="back-button"
          onClick={() => navigate("/admin/blogs")}
        >
          <FaArrowLeft />
          Back to Blogs
        </button>
      </div>
    );
  }

  // ============================================================
  // PAGE
  // ============================================================

  return (
    <div className="blog-form-page">
      {/* HEADER */}

      <div className="blog-form-header">
        <button
          type="button"
          className="back-button"
          onClick={() => navigate("/admin/blogs")}
        >
          <FaArrowLeft />
          Back to Blogs
        </button>

        <h2>Edit Blog</h2>

        <p>Update your blog article.</p>
      </div>

      {error && <div className="blog-form-error">{error}</div>}

      <form className="blog-form" onSubmit={handleSubmit}>
        {/* BASIC INFORMATION */}

        <div className="blog-form-card">
          <div className="blog-form-card-header">
            <h3>Basic Information</h3>

            <p>Update your article information.</p>
          </div>

          <div className="blog-form-body">
            <div className="form-group">
              <label>Blog Title *</label>

              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleChange}
                disabled={saving}
              />
            </div>

            <div className="form-group">
              <label>URL Slug</label>

              <input
                type="text"
                name="slug"
                value={formData.slug}
                onChange={handleChange}
                disabled={saving}
              />
            </div>

            <div className="form-group">
              <label>Category *</label>

              <input
                type="text"
                name="category"
                value={formData.category}
                onChange={handleChange}
                disabled={saving}
              />
            </div>

            <div className="form-group">
              <label>Author</label>

              <input
                type="text"
                name="author"
                value={formData.author}
                onChange={handleChange}
                disabled={saving}
              />
            </div>

            <div className="form-group">
              <label>Publication Date</label>

              <input
                type="date"
                name="date"
                value={formData.date}
                onChange={handleChange}
                disabled={saving}
              />
            </div>

            <div className="form-group">
              <label>Short Description *</label>

              <textarea
                name="excerpt"
                value={formData.excerpt}
                onChange={handleChange}
                rows="4"
                disabled={saving}
              />
            </div>

            <div className="form-group">
              <label>Blog Content *</label>

              <textarea
                name="content"
                value={formData.content}
                onChange={handleChange}
                rows="14"
                disabled={saving}
              />
            </div>
          </div>
        </div>

        {/* IMAGE */}

        <div className="blog-form-card">
          <div className="blog-form-card-header">
            <h3>
              <FaImage />
              Featured Image
            </h3>
          </div>

          <div className="blog-form-body">
            <div className="form-group">
              <label>Change Image</label>

              <input
                type="file"
                accept="image/*"
                onChange={handleImageChange}
                disabled={saving}
              />
            </div>

            {imagePreview && (
              <div className="image-preview">
                <img src={imagePreview} alt="Blog preview" />
              </div>
            )}
          </div>
        </div>

        {/* TAGS */}

        <div className="blog-form-card">
          <div className="blog-form-card-header">
            <h3>Tags</h3>
          </div>

          <div className="blog-form-body">
            <div className="form-group">
              <label>Blog Tags</label>

              <input
                type="text"
                name="tags"
                value={formData.tags}
                onChange={handleChange}
                placeholder="Acrylic, PU, Sports Flooring"
                disabled={saving}
              />
            </div>
          </div>
        </div>

        {/* SEO */}

        <div className="blog-form-card">
          <div className="blog-form-card-header">
            <h3>SEO Settings</h3>
          </div>

          <div className="blog-form-body">
            <div className="form-group">
              <label>SEO Title</label>

              <input
                type="text"
                name="seoTitle"
                value={formData.seoTitle}
                onChange={handleChange}
                disabled={saving}
              />
            </div>

            <div className="form-group">
              <label>SEO Description</label>

              <textarea
                name="seoDescription"
                value={formData.seoDescription}
                onChange={handleChange}
                rows="4"
                disabled={saving}
              />
            </div>
          </div>
        </div>

        {/* PUBLISHING */}

        <div className="blog-form-card">
          <div className="blog-form-card-header">
            <h3>Publishing</h3>
          </div>

          <div className="blog-form-body">
            <div className="form-group">
              <label>Status</label>

              <select
                name="status"
                value={formData.status}
                onChange={handleChange}
                disabled={saving}
              >
                <option value="draft">Draft</option>

                <option value="published">Published</option>
              </select>
            </div>

            <label className="publish-toggle">
              <input
                type="checkbox"
                name="featured"
                checked={formData.featured}
                onChange={handleChange}
                disabled={saving}
              />

              <span>Mark this blog as featured</span>
            </label>
          </div>
        </div>

        {/* ACTIONS */}

        <div className="blog-form-actions">
          <button
            type="button"
            className="cancel-blog-btn"
            onClick={() => navigate("/admin/blogs")}
            disabled={saving}
          >
            Cancel
          </button>

          <button type="submit" className="save-blog-btn" disabled={saving}>
            <FaSave />

            {saving
              ? "Saving..."
              : formData.status === "published"
                ? "Save & Publish"
                : "Save Changes"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default EditBlog;
